import Axios from "axios";
import Vue from "vue";
import i18n from "../../i18n";
import _ from "lodash";
import { API_HOST } from "../../config/app-const";
import { normalize } from "../../helpers/pagination";
import { CATEGORY_TYPES } from "../../config/const";
import array from "../../helpers/array";
import { getUrlParams } from '../../helpers/pagination';
import localStorage from '../../helpers/localStorage';

const state = {
  projectSetup: {
    isBusy: false,
    data: null,
    error: null,
    isReady: true,
    backup: null,
    sourceIsBusy: false,
    importedOutlets: null,
    isImportOutletsBusy: false,
    isUpdateTagsBusy: false,
    abortController: null
  },
  relationships: {
    isBusy: false,
    data: null,
    error: null
  },
  projectCategorySetup: {
    isBusy: false,
    data: null,
    error: null,
    pagination: null
  },
  projectHierarchy: {
    isBusy: false,
    data: [],
    error: null,
    pagination: null
  },
  projectAssociation: {
    isBusy: false,
    data: [],
    error: null,
    pagination: null
  }
};

const getters = {
  CATEGORIES: state => {
    return state?.projectCategorySetup?.data || [];
  },
  CATEGORIES_WITHOUT_ORGANIZATION: (state, getters) => {
    return getters.CATEGORIES.filter(category => {
      return category.category_type_id !== CATEGORY_TYPES.ORGANIZATION;
    });
  },
  CATEGORIES_CAN_ADD_TAGS: (state, getters) => {
    return getters.CATEGORIES.filter(category => {
      return getters.CATEGORIES_WITHOUT_ORGANIZATION.indexOf(category) > -1 && !category.scoring_default;
    })
  },
  AFFILIATIONS: state => {
    return state.projectSetup.data && state.projectSetup.data.affiliations || [];
  },
  favourability_methods: state => {
    let methods = [...state.projectSetup.data.favourability_methods]
    return methods.sort((a, b) => (a.id > b.id) ? 1 : -1)
  },
  split_scoring() {
    const item_order = ["four","three","two"];
    const split_scoring = [...state.projectSetup.data.split_scoring]
    return split_scoring.sort((a, b) => item_order.indexOf(a.text.toLowerCase()) - item_order.indexOf(b.text.toLowerCase()));
  },
  project_setup_is_busy(state){
    return state?.projectSetup?.isBusy;
  },
  projectSetup(state){
    return state.projectSetup?.data;
  }
}

const mutations = {
  // ALL PROJECT SETUP-----------------------------
  LOAD_PROJECT_SETUP_BUSY: (state, payload) => {
    state.projectSetup.isBusy = payload;
  },
  LOAD_PROJECT_SETUP_SUCCESS: (state, payload) => {
    if(!payload.general?.organizations_with_category){
      payload = Object.assign({} ,payload, { categories: payload.categories.map(c => {
          c.tags = c.tags.filter(t => t !== null);
          return c;
        })
      });
    }
    state.projectSetup.data = payload;
    state.projectSetup.error = null;
  },
  LOAD_PROJECT_SETUP_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
    state.projectSetup.data = null;
  },
  LOAD_PROJECT_CATEGORIES_SETUP_BUSY: (state, payload) => {
    state.projectCategorySetup.isBusy = payload;
  },
  LOAD_PROJECT_CATEGORIES_SETUP_SUCCESS: (state, payload) => {
    if(!payload?.isCategoryExists){
      state.projectCategorySetup.data = payload?.data;
      state.projectCategorySetup.pagination = {};
      state.projectCategorySetup.error = null;
      return;
    }

    const categoriesTags = [
      ...state.projectCategorySetup?.data || [],
      ...payload?.data || [],
    ].reduce((acc, item) => {
      // Find if there's an existing item with the same id
      const existingItemIndex = acc.findIndex(accItem => accItem?.id === item?.id);
    
      if (existingItemIndex !== -1) {
        const existingTags = payload?.reset_state_tags ? [] : acc[existingItemIndex]?.tags || [];
        const newTags = item.tags || [];

        // Combine the existing and new tags
        const combinedTags = [...existingTags, ...newTags];

        // Use a Map to remove duplicates based on a unique key (e.g., 'id')
        const uniqueTags = Array.from(
          new Map(combinedTags.map(tag => [tag?.id, tag])).values()
        );
        
        // Remove duplicates within the tags array
        acc[existingItemIndex] = {
          ...acc[existingItemIndex],
          tags: uniqueTags, 
        };
      } else {
        acc.push({
          ...item,
          tags: Array.from(new Map(item?.tags?.map(tag => [tag?.id, tag])).values())
        });
      }
    
      return acc;
    }, []);
    state.projectCategorySetup.data = categoriesTags;
    state.projectCategorySetup.pagination = payload?.pagination
    state.projectCategorySetup.error = null;
  },
  LOAD_PROJECT_CATEGORIES_SETUP_FAIL: (state, payload) => {
    state.projectCategorySetup.error = payload;
    state.projectCategorySetup.data = null;
  },
  LOAD_PROJECT_HIERARCHY_BUSY: (state, payload) => {
    state.projectHierarchy.isBusy = payload;
  },
  LOAD_PROJECT_HIERARCHY_SUCCESS: (state, payload) => {
    state.projectHierarchy.data = !payload?.pagination || payload?.pagination?.currentPage === 1 ? payload?.data : [...state.projectHierarchy.data, ...payload?.data];
    state.projectHierarchy.pagination = payload?.pagination || {};
    state.projectHierarchy.error = null;
  },
  LOAD_PROJECT_HIERARCHY_FAIL: (state, payload) => {
    state.projectHierarchy.error = payload;
    state.projectHierarchy.data = null;
  },
  LOAD_PROJECT_ASSOCIATION_BUSY: (state, payload) => {
    state.projectAssociation.isBusy = payload;
  },
  LOAD_PROJECT_ASSOCIATION_SUCCESS: (state, payload) => {
    state.projectAssociation.data = !payload?.pagination || payload?.pagination?.currentPage === 1 ? payload?.data : [...state.projectAssociation.data, ...payload?.data];
    state.projectAssociation.pagination = payload?.pagination || {};
    state.projectAssociation.error = null;
  },
  LOAD_PROJECT_ASSOCIATION_FAIL: (state, payload) => {
    state.projectAssociation.error = payload;
    state.projectAssociation.data = null;
  },
  UPDATE_PROJECT_SETUP_BUSY(state, payload) {
    state.projectSetup.isBusy = payload;
  },
  UPDATE_PROJECT_SETUP_SUCCESS(state, payload) {
    state.projectSetup.data.general = payload;
    state.projectSetup.error = null;
  },
  UPDATE_PROJECT_SETUP_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
    state.projectSetup.data.general = null;
  },
  CREATE_PROJECT_SETUP_BUSY(state, payload) {
    state.projectSetup.isBusy = payload;
  },
  CREATE_PROJECT_SETUP_SUCCESS(state, payload) {
    state.projectSetup.data = payload;
    state.projectSetup.error = null;
  },
  CREATE_PROJECT_SETUP_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
    state.projectSetup.data = null;
  },
  DELETE_PROJECT_SETUP_BUSY(state, payload) {
    state.projectSetup.isBusy = payload;
  },
  DELETE_PROJECT_SETUP_SUCCESS(state, payload) {
    state.projectSetup.data = payload;
    state.projectSetup.error = null;
  },
  DELETE_PROJECT_SETUP_FAIL(state, payload) {
    state.projectSetup.error = payload;
    state.projectSetup.data = null;
  },
  SET_PROJECT_SETUP_ABORT_CONTROLLER(state, controller) {
    state.projectSetup.abortController = controller;
  },
  CLEAR_PROJECT_SETUP_ABORT_CONTROLLER(state) {
    state.projectSetup.abortController = null;
  },
  //------------------------------------------------------------------

  //PROJECT SETUP - TAG-----------------------------------------------
  LOAD_TAG_BUSY: (state, payload) => {
    state.projectSetup.isBusy = payload;
  },
  LOAD_TAG_SUCCESS: (state, payload) => {
    const catIndex = state.projectCategorySetup.data?.findIndex( c => c.id == payload.cat_id );
    state.projectCategorySetup.data[catIndex].tags = payload;
    state.projectSetup.error = null;
  },
  LOAD_TAG_FAIL: (state, payload) => {
    const catIndex = state.projectCategorySetup.data?.findIndex( c => c.id == payload.cat_id );
    state.projectSetup.error = payload;
    state.projectCategorySetup.data[catIndex].tags = null;
  },
  UPDATE_TAG_BUSY(state, payload) {
    state.projectSetup.isUpdateTagsBusy = payload;
  },
  UPDATE_TAG_PRE_PROCESS(state, payload) {
    if(state.projectSetup.data.tags){
      const tagIndex = state.projectSetup.data.tags.findIndex( t => t.id == payload.id );
      if (tagIndex !== -1) {
        // Use Vue.set to ensure reactivity and avoid mutation warnings
        Vue.set(state.projectSetup.data.tags, tagIndex, {
          ...state.projectSetup.data.tags[tagIndex],
          ...payload
        });
      }
    }
  },
  UPDATE_TAG_SUCCESS(state, payload) {
   if (state.projectSetup.data && state.projectSetup.data.tags){
      if (payload?.[0]?.duplicated_tag) {
        const duplicatedTagIds = new Set(payload.map((t) => t.id));
        const remainingTags = state.projectSetup.data.tags.filter((t) => !duplicatedTagIds.has(t.id));
        state.projectSetup.data.tags = [...payload, ...remainingTags];
      } else {
        const tagIndex = state.projectSetup.data.tags.findIndex( t => t.id == payload.id );
        Vue.set(state.projectSetup.data.tags, tagIndex, {...state.projectSetup.data.tags?.[tagIndex], ...payload});
        state.projectSetup.error = null;
      }
    }
    if (state.projectSetup.data && state.projectCategorySetup.data){
      const catIndex = state.projectCategorySetup.data?.findIndex( c => c.id == payload.cat_id );
      const tagIndex = state.projectCategorySetup.data?.[catIndex]?.tags?.findIndex( t => t.id == payload.id );
      Vue.set(state.projectCategorySetup.data[catIndex].tags, tagIndex, payload);
      state.projectSetup.error = null;
    }
  },
  UPDATE_TAG_CATEGORY: (state, { newCategoryId, tagId, categoryId, tagKey }) => {

    state.projectSetup.backup = _.cloneDeep(state.projectSetup.data);

    const category = state.projectCategorySetup.data.find(category => {
      return category.id === categoryId;
    });

    const tagIndex = category.tags.findIndex(tag => {
      return tag.id === tagId;
    });

    const categoryToAdd = state.projectCategorySetup.data.find(category => {
      return category.id === newCategoryId;
    });

    category.tags[tagIndex].cat_id = newCategoryId;
    category.tags[tagIndex].tag_key = tagKey;

    categoryToAdd.tags.push(category.tags[tagIndex]);

    category.tags.splice(tagIndex, 1);
  },
  UPDATE_TAG_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
    const catIndex = state.projectCategorySetup.data?.findIndex( c => c.id == payload?.cat_id );
    if (state.projectCategorySetup.data?.[catIndex]) {
      state.projectCategorySetup.data[catIndex].tags = null;
    }
  },
  CREATE_TAG_BUSY(state, payload) {
    state.projectSetup.isBusy = payload;
  },
  CREATE_TAG_SUCCESS(state, payload) {
    const categoryIndex = state.projectCategorySetup.data.findIndex(
      c => c.id == payload.cat_id
    );

    const categories = JSON.parse(JSON.stringify(state.projectCategorySetup.data));
    //const tags = categories[categoryIndex].tags.filter(t => t !== null);
    const tags = categories[categoryIndex].tags;

    categories[categoryIndex].tags = [...tags, payload];

    state.projectCategorySetup.data = [...categories];
    state.projectSetup.error = null;
  },
  CREATE_TAG_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
    let categoryIndex = state.projectCategorySetup.data?.findIndex(c => c.id == payload?.cat_id)
    if(state.projectCategorySetup.data?.[categoryIndex]) state.projectCategorySetup.data[categoryIndex].tags = null;
  },
  DELETE_TAG_BUSY(state, payload) {
    state.projectSetup.isBusy = payload;
  },
  DELETE_TAG_SUCCESS(state, payload) {
    const projectSetupCategory = state.projectCategorySetup.data;

    let categoryIndex, tags;
    if (_.get(projectSetupCategory,'data')) {
      categoryIndex = projectSetupCategory.data.findIndex(c => c.id == payload.cat_id)
      
      if (projectSetupCategory.data[categoryIndex].tags){
        tags = projectSetupCategory.data[categoryIndex].tags.filter(c => c.id != payload.id);
        projectSetupCategory.data[categoryIndex].tags = tags;
      }
    }
    else if (projectSetupCategory) {
      categoryIndex = projectSetupCategory.findIndex(c => c.id == payload.cat_id);
      if (projectSetupCategory[categoryIndex].tags){
        tags = projectSetupCategory[categoryIndex].tags.filter(c => c.id != payload.id);
        projectSetupCategory[categoryIndex].tags = tags;
      }
    }
    if (state.projectSetup.data.tags){
      tags = state.projectSetup.data.tags.filter(c => c.id != payload.id)
    }
    let category = state.projectSetup.data;
    state.projectSetup.data = category;
    state.projectSetup.data.tags = tags;
    state.projectSetup.error = null;
  },
  DELETE_TAG_FAIL(state, payload) {
    state.projectSetup.error = payload;
    const catIndex = state.projectCategorySetup.data?.findIndex( c => c.id == payload.cat_id );
    state.projectCategorySetup.data[catIndex].tags = null;
  },
  LOAD_CATEGORY_TAGS_BUSY(state, payload){
    Vue.set(state.projectSetup, 'isTagsBusy', payload);
  },
  CREATE_CATEGORY_TAGS(state, payload){
    if (state.projectSetup.data) Vue.set(state.projectSetup.data, 'tags', payload);
  },
  LOAD_CATEGORY_TAGS_SUCCESS(state, { tags, pagination }) {
    if (!state.projectSetup.data) return;
    const currentPage = Number(pagination?.currentPage);
    if (pagination != null && currentPage === 1) {
      Vue.set(state.projectSetup.data, 'tags', tags);
    } else {
      const existing = [...(state.projectSetup.data.tags || [])];
      const ids = new Set(existing.map((t) => t.id));
      tags.forEach((t) => {
        if (!ids.has(t.id)) {
          ids.add(t.id);
          existing.push(t);
        }
      });
      Vue.set(state.projectSetup.data, 'tags', existing);
    }
    Vue.set(state.projectSetup.data, 'tagsPagination', pagination);
  },
  DELETE_CATEGORY_BULK_TAGS_SUCCESS: (state, payload) => {
    if (Array.isArray(payload) && payload.length > 0) {
      state.projectCategorySetup.data = state.projectCategorySetup.data.map((c)=> c.id === payload[0].cat_id ? Object.assign({}, c , {tags: c.tags.filter(t => !payload.some(p => p.id === t.id)) }) : c);
    }
  },
  DELETE_SOURCES_SUCCESS: (state, payload) => {
    const sourceIds = payload.map(({id}) => id)
    state.projectSetup.data.sources = state.projectSetup.data.sources.filter(source => !sourceIds.includes(source.id))
  },
  DELETE_PROJECT_BULK_TAGS_SUCCESS: (state, payload) => {
    if (Array.isArray(payload) && payload.length > 0) {
      state.projectCategorySetup.data.data = state.projectCategorySetup.data.data.map((c)=> c.id === payload[0].cat_id ? Object.assign({}, c , {tags: c.tags.filter(t => !payload.some(p => p.id === t.id)) }) : c);
    }
  },
  DELETE_PROJECT_BULK_TAGS_BUSY: (state, payload) => {
    state.projectSetup.isBusy = payload;
  },
  DELETE_PROJECT_BULK_TAGS_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
    state.projectSetup.data = null;
  },
  DELETE_CATEGORY_BULK_TAGS_BUSY: (state, payload) => {
    state.projectSetup.isBusy = payload;
  },
  DELETE_CATEGORY_BULK_TAGS_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
    state.projectSetup.data = null;
  },
  LOAD_CATEGORY_TAGS_IS_READY: (state, payload) => {
    state.projectSetup.isReady = payload;
  },
  LOAD_CATEGORY_TAGS_FAIL(state, payload){
    state.projectSetup.error = payload;
    const catIndex = state.projectCategorySetup.data?.findIndex( c => c.id == payload.cat_id );
    if(state.projectCategorySetup.data && state.projectCategorySetup.data[catIndex]) {
      state.projectCategorySetup.data[catIndex].tags = null;
    }
  },
  UPDATE_PROJECT_BULK_TAGS_BUSY(state, payload) {
    state.projectSetup.isBusy = payload;
  },
  UPDATE_PROJECT_BULK_TAGS_SUCCESS(state, payload) {
    state.projectSetup.error = null;
    if(Array.isArray(payload) && payload.length > 0){
      state.projectCategorySetup.data.data = state.projectCategorySetup.data.data.map((c) => c.id === payload[0].cat_id  ? Object.assign({}, c , {tags: payload}) : c);
    }
  },
  UPDATE_PROJECT_CATEGORIES_BULK_SOURCES_SUCCESS(state, payload) {
    state.projectSetup.error = null;
    if(Array.isArray(payload) && payload.length > 0) {
    const tagIds = payload.map(tag => tag.id)
     let targetCategory = state.projectCategorySetup.data.data.find((c) => c.id === payload[0].cat_id);
     if(targetCategory) {
       targetCategory.tags = targetCategory.tags.map(tag => tagIds.includes(tag.id) ? payload.find(item => item.id === tag.id) : tag)
     }
    }
  },
  UPDATE_PROJECT_BULK_TAGS_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
    state.projectSetup.data = null;
  },
  MERGE_TAGS_BUSY(state, payload) {
    state.projectSetup.isBusy = payload;
  },
  MERGE_TAGS_SUCCESS(state, {update, remove}) {
    const removeIds = Array.isArray(remove) ? remove.map(item => item?.id) : [];
    const data = state.projectCategorySetup.data || state.projectSetup.data;
    if (_.get(data, 'data')) {
      const category = data.data.find(cat => cat.id === update.cat_id);
      category.tags = array.filterByIds(category.tags, removeIds);

      const tagToUpdate = category.tags.find(tag => tag.id === update.id);
      tagToUpdate.article_count = update.article_count;
    }

    if (data.tags && data.tags.length) {
      const tag = data.tags.find(tag => tag.id === update.id);

      if (tag) tag.article_count = update.article_count;
    }

    data.tags = array.filterByIds(data.tags, removeIds);
  },
  MERGE_TAGS_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
  },
  MERGE_SOURCES_SUCCESS(state, {update, remove}) {
    const removeIds = remove.map(item => item.id);
    const data = state.projectCategorySetup.data || state.projectSetup.data;
    if (_.get(data, 'data')) {
      const category = data.data.find(cat => cat.id === update.cat_id);
      category.tags = array.filterByIds(category.tags, removeIds);

      const tagToUpdate = category.tags.find(tag => tag.id === update.id);
      tagToUpdate.article_count = update.article_count;
    }

    if (data.sources && data.sources.length) {
      const source = data.sources.find(source => source.id === update.id);

      if (source) source.article_count = update.article_count;
    }

    data.sources = array.filterByIds(data.sources, removeIds);
  },
  UPDATE_PROJECT_SOURCE_SUCCESS(state, payload) {
    state.projectSetup.error = null;
    payload.forEach(source => {
      let targetIndex = state.projectSetup.data.sources.findIndex(item => item.id === source.id)
      if(targetIndex != -1) state.projectSetup.data.sources.splice(targetIndex, 1, source)
    })
  },

  LOAD_TAGS_USAGE_REPORT_BUSY(state, payload){
    state.projectSetup = {...state.projectSetup, isUsageReportBusy: payload};
  },
  
  LOAD_TAGS_USAGE_REPORT_PAGINATION_BUSY(state, payload){
    state.projectSetup = {...state.projectSetup, isUsageReportPaginationBusy: payload};
  },

  LOAD_TAGS_USAGE_REPORT_SUCCESS(state, {tags, pagination}){
    if(!state.projectSetup.data){
      state.projectSetup.data = {tags: []}
    }
    if(pagination.currentPage === 1){
      state.projectSetup.data.tags = tags
    }else{
      state.projectSetup.data.tags.push.apply( 
        state.projectSetup.data.tags, tags
      );
    }
    Vue.set(state.projectSetup.data, 'tagsPagination', pagination);
  },

  LOAD_TAGS_USAGE_REPORT_FAIL(state, payload){
  },
  //------------------------------------------------------------------

  // PROJECT SETUP - GENERAL------------------------------------------
  UPDATE_GENERAL_BUSY(state, payload) {
    state.projectSetup.isBusy = payload;
  },
  UPDATE_GENERAL_SUCCESS(state, payload) {
    state.projectSetup.data.general = payload;
    state.projectSetup.error = null;
  },
  UPDATE_GENERAL_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
    state.projectSetup.data.general = null;
  },
  //-----------------------------------------------------------------

  //PROJECT SETUP - ORGANIZATIONS------------------------------------
  UPDATE_ORGANIZATIONS_BUSY(state, payload) {
    state.projectSetup.isBusy = payload;
  },
  UPDATE_ORGANIZATIONS_SUCCESS(state, payload) {
    const organizationIndex = state.projectSetup.data.organizations.organization.findIndex(
      c => c.id == payload.id
    );
    state.projectSetup.data.organizations.organization[organizationIndex].name =
      payload.value;

    state.projectSetup.error = null;
  },
  UPDATE_ORGANIZATIONS_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
    state.projectSetup.data.organizations = null;
  },
  CREATE_ORGANIZATIONS_BUSY(state, payload) {
    state.projectSetup.isBusy = payload;
  },
  CREATE_ORGANIZATIONS_SUCCESS(state, payload) {
    payload.name = "";
    state.projectSetup.data.organizations.organization.push(payload);
    state.projectSetup.error = null;
  },
  CREATE_ORGANIZATIONS_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
    state.projectSetup.data.organizations = null;
  },
  DELETE_ORGANIZATIONS_BUSY(state, payload) {
    state.projectSetup.isBusy = payload;
  },
  DELETE_ORGANIZATIONS_SUCCESS(state, payload) {
    const organizationIndex = state.projectSetup.data.organizations.organization.findIndex(
      c => c.id == payload
    );
    state.projectSetup.data.organizations.organization.splice(
      organizationIndex,
      1
    );
    state.projectSetup.error = null;
  },
  DELETE_ORGANIZATIONS_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
    state.projectSetup.data.organizations = null;
  },
  //------------------------------------------------
  //PROJECT SETUP - AUTHORS----------------------
  LOAD_AUTHORS_BUSY: (state, payload) => {
    state.projectSetup.isBusy = payload;
  },
  LOAD_AUTHORS_IS_READY: (state, payload) => {
    state.projectSetup.isReady = payload;
  },
  LOAD_AUTHORS_SUCCESS: (state, payload) => {
    state.projectSetup.data = {
      ...state.projectSetup.data,
      authors: payload
    };
    state.projectSetup.error = null;
  },
  LOAD_AUTHORS_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
    state.projectSetup.data = null;
  },

  //PROJECT SETUP - CATEGORIES----------------------
  LOAD_CATEGORIES_BUSY: (state, payload) => {
    state.projectSetup.isBusy = payload;
    Vue.set(state.projectSetup, 'isCategoriesBusy', payload);
  },
  LOAD_CATEGORIES_IS_READY: (state, payload) => {
    state.projectSetup.isReady = payload;
  },
  LOAD_CATEGORIES_SUCCESS: (state, payload) => {
    state.projectSetup.data = {
      ...state.projectSetup.data,
      categories: payload
    };
    state.projectSetup.error = null;
  },
  LOAD_CATEGORIES_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
    state.projectSetup.data = null;
  },
  UPDATE_CATEGORIES_BUSY(state, payload) {
    state.projectSetup.isBusy = payload;
  },
  UPDATE_CATEGORIES_SUCCESS(state, payload) {
    state.projectCategorySetup.data = [
      ...state.projectCategorySetup.data?.map(c => c.id === payload.id ? { ...c, ...payload } : c)
    ]
    state.projectSetup.error = null;
  },
  UPDATE_CATEGORIES_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
    state.projectCategorySetup.data = null;
  },
  CREATE_CATEGORIES_BUSY(state, payload) {
    state.projectSetup.isBusy = payload;
  },
  CREATE_CATEGORIES_SUCCESS(state, payload) {
    state.projectCategorySetup.data?.push(payload);
    state.projectSetup.error = null;
  },
  CREATE_CATEGORIES_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
    state.projectCategorySetup.data = null;
  },
  DELETE_CATEGORIES_BUSY(state, payload) {
    state.projectSetup.isBusy = payload;
  },
  DELETE_CATEGORIES_SUCCESS(state, payload) {
    state.projectCategorySetup.data = state.projectCategorySetup.data?.filter(
      c => c.id != payload
    );
    state.projectSetup.error = null;
  },
  DELETE_CATEGORIES_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
    state.projectCategorySetup.data = null;
  },
  //------------------------------------------------

  // PROJECT SETUP - SCORING------------------------
  UPDATE_SCORING_BUSY(state, payload) {
    state.projectSetup.isBusy = payload;
  },
  UPDATE_SCORING_SUCCESS(state, payload) {
    state.projectSetup.data.scoring = payload;
    state.projectSetup.error = null;
  },
  UPDATE_SCORING_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
    state.projectSetup.data.scoring = null;
  },
  //------------------------------------------------

  ADD_RELATIONSHIP_BUSY: (state, payload) => {
    state.relationships.isBusy = payload;
  },
  ADD_RELATIONSHIP_FAIL: (state, payload) => {
    state.relationships.error = payload;
  },

  UPDATE_RELATIONSHIP_BUSY: (state, payload) => {
    state.relationships.isBusy = payload;
  },
  ADD_OR_UPDATE_RELATIONSHIP_SUCCESS: (state, payload) => {
    state.relationships.data = payload;
    state.relationships.error = null;

    if (payload.type == "hierarchy") {
      state.projectHierarchy.data = payload.data;
    } else if (payload.type == "association") {
      // If payload.data is a single object, update parent if exists
      if (payload.data && !Array.isArray(payload.data)) {
        const index = state.projectAssociation.data.findIndex(item => item.id === payload.data.id);
        if (index !== -1) {
          Vue.set(state.projectAssociation.data, index, payload.data);
        }
        // If parent doesn't exist, ignore it (don't add)
      } else {
        state.projectAssociation.data = payload.data;
      }
    }
  },
  UPDATE_RELATIONSHIP_FAIL: (state, payload) => {
    state.relationships.error = payload;
  },

  REMOVE_RELATIONSHIP_BUSY: (state, payload) => {
    state.relationships.isBusy = payload;
  },
  REMOVE_RELATIONSHIP_SUCCESS: (state, payload) => {
    state.relationships.data = payload;
    state.relationships.error = null;
    if (payload.type == "hierarchy") {
      state.projectHierarchy.data = state.projectHierarchy.data?.filter(
        t =>
          t.tags?.find(t => t?.id != payload.relationship.tag_id) ||
          t.parent_tag_id != payload.relationship.parent_tag_id
      );
    } else if (payload.type == "association") {
      const parent = state.projectAssociation.data?.find(item => item.id === payload.relationship.id);

      const idsToRemove = payload.relationship.delete_associations;

      parent.child_tags = parent.child_tags
        .filter(child => !idsToRemove.includes(child.association_id));
    }
  },
  REMOVE_RELATIONSHIP_FAIL: (state, payload) => {
    state.relationships.error = payload;
  },

  UPDATE_GROUPING_BUSY: (state, payload) => {
    state.relationships.isBusy = payload;
  },
  UPDATE_GROUPING_SUCCESS: (state, payload) => {
    state.relationships.data = payload;
    state.relationships.error = null;
    state.projectSetup.data.groupings = payload;
  },
  UPDATE_GROUPING_FAIL: (state, payload) => {
    state.relationships.error = payload;
  },

  LOAD_GROUPINGS_BUSY: (state, payload) => {
    state.relationships.isBusy = payload;
  },
  LOAD_GROUPINGS_SUCCESS: (state, payload) => {
    state.projectSetup.error = null;
    state.projectSetup.data = Object.assign({}, state.projectSetup.data, {
      groupings: payload
    });
  },
  LOAD_GROUPINGS_IS_READY: (state, payload) => {
    state.relationships.isReady = payload;
  },
  LOAD_GOUPINGS_FAIL: (state, payload) => {
    state.relationships.error = payload;
  },

  LOAD_SOURCES_BUSY: (state, payload) => {
    state.projectSetup.sourceIsBusy = payload;
  },
  LOAD_SOURCES_SUCCESS: (state, { data, pagination }) => {
    state.projectSetup.error = null;
    if (!state.projectSetup.data) state.projectSetup.data = {};
    if (!_.get(state.projectSetup, 'data.sources')) {
      Vue.set(state.projectSetup.data, 'sources', []);
    }
    const currentPage = Number(pagination?.currentPage);
    if (pagination != null && currentPage === 1) {
      state.projectSetup.data.sources = [];
    }
    const ids = new Set(state.projectSetup.data.sources.map((s) => s.id));
    data.forEach((source) => {
      if (!ids.has(source.id)) {
        ids.add(source.id);
        state.projectSetup.data.sources.push(source);
      }
    });
    Vue.set(state.projectSetup.data, 'sourcesPagination', pagination);
  },
  LOAD_SOURCES_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
  },

  ADD_SOURCE_BUSY: (state, payload) => {
    state.projectSetup.isBusy = payload;
  },
  ADD_SOURCE_SUCCESS: (state, payload) => {
    state.relationships.error = null;
    // state.projectSetup.data.sources.push(payload);
    state.projectSetup.data = Object.assign({}, state.projectSetup.data, {
      sources: [...state.projectSetup.data.sources, payload]
    });
  },
  ADD_SOURCE_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
  },

  UPDATE_SOURCE_BUSY: (state, payload) => {
    state.projectSetup.isBusy = payload;
  },
  UPDATE_SOURCE_SUCCESS: (state, payload) => {
    state.relationships.error = null;
    const sources = state.projectSetup.data.sources.map(s =>
      s.id == payload.id ? payload : s
    );
    state.projectSetup.data = Object.assign({}, state.projectSetup.data, {
      sources
    });
  },
  UPDATE_SOURCE_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
  },

  REMOVE_SOURCE_BUSY: (state, payload) => {
    state.projectSetup.isBusy = payload;
  },
  REMOVE_SOURCE_SUCCESS: (state, payload) => {
    state.relationships.error = null;
    state.projectSetup.data.sources = state.projectSetup.data.sources.filter(
      s => s.id != payload
    );
  },
  REMOVE_SOURCE_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
  },

  LOAD_SOURCES_AFFILIATIONS_BUSY: (state, payload) => {
    state.projectSetup.isBusy = payload;
  },
  LOAD_SOURCES_AFFILIATIONS_SUCCESS: (state, payload) => {
    state.projectSetup.error = null;
    state.projectSetup.data = Object.assign({}, state.projectSetup.data, {
      affiliations: payload
    });
  },
  LOAD_SOURCES_AFFILIATIONS_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
  },
  //PROJECT SETUP - CATEGORY GROUPING-----------------------------------------------
  LOAD_CATEGORY_GROUPING_BUSY: (state, payload) => {
    state.projectSetup.isBusy = payload;
  },
  LOAD_CATEGORY_GROUPING_SUCCESS: (state, payload) => {
    const categories = state.projectCategorySetup.data?.map(c => c.id === payload.id ? { ...c, ...payload } : c);

    const data = { ...state.projectSetup.data };
    data.categories = [ ...categories ];

    state.projectSetup.data = data;
    state.projectSetup.error = null;
  },
  LOAD_CATEGORY_GROUPING_IS_READY: (state, payload) => {
    state.projectSetup.isReady = payload;
  },
  LOAD_CATEGORY_GROUPING_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
    const catIndex = state.projectCategorySetup.data?.findIndex( c => c.id == payload.cat_id );
    state.projectCategorySetup.data[catIndex].grouping = null;
  },
  UPDATE_CATEGORY_GROUPING_BUSY(state, payload) {
    state.projectSetup.isBusy = payload;
  },
  UPDATE_CATEGORY_GROUPING_SUCCESS(state, payload) {
    const grouping = payload;
    const categoryIndex = state.projectCategorySetup.data?.findIndex(
      c => c.id == grouping.cat_id
    );

    const groupingIndex = state.projectCategorySetup.data[categoryIndex].tags.findIndex(t => t.id == grouping.id);

    state.projectCategorySetup.data[categoryIndex].tags[
      groupingIndex
    ] = payload;

    state.projectSetup.error = null;
  },
  UPDATE_CATEGORY_GROUPING_FAIL: (/*state, payload*/) => {
    // state.projectSetup.error = payload;
    // state.projectCategorySetup.data.grouping = null;
  },
  CREATE_CATEGORY_GROUPING_BUSY(state, payload) {
    state.projectSetup.isBusy = payload;
  },
  CREATE_CATEGORY_GROUPING_SUCCESS(state, payload) {
    const categoryIndex = state.projectCategorySetup.data.findIndex(
      c => c.id == payload.cat_id
    );

    state.projectCategorySetup.data[categoryIndex].tags.push(payload);
    state.projectSetup.error = null;
  },
  CREATE_CATEGORY_GROUPING_FAIL: (/*state, payload*/) => {
    //state.projectSetup.error = payload;
    //state.projectCategorySetup.data.tags = null;
  },
  DELETE_CATEGORY_GROUPING_BUSY(state, payload) {
    state.projectSetup.isBusy = payload;
  },
  DELETE_CATEGORY_GROUPING_SUCCESS(state, payload) {
    state.projectCategorySetup.data = state.projectCategorySetup.data.map(c => c.id === payload.cat_id ? Object.assign({}, c, {tags: c.tags.filter(t => t.id !== payload.id)}): c);
    state.projectSetup.error = null;
  },
  DELETE_CATEGORY_GROUPING_FAIL(state, payload) {
    state.projectSetup.error = payload;
    const catIndex = state.projectCategorySetup.data?.findIndex( c => c.id == payload.cat_id );
    state.projectCategorySetup.data[catIndex].grouping = null;
  },



  BULK_UPDATE_CATEGORY_TAG_COUNTS_BUSY(state, payload) {
    state.projectSetup.data.tagCountBusy = payload;
  },
  BULK_UPDATE_CATEGORY_TAG_COUNTS_SUCCESS(state, payload) {
    state.projectCategorySetup.data.find(function(r){
      return r.id == payload.cat_id
    }).tag_count = payload.tags;
  },
  BULK_UPDATE_CATEGORY_TAG_COUNTS_FAIL(state, payload) {
    state.projectSetup.error = payload;
    const catIndex = state.projectCategorySetup.data?.findIndex( c => c.id == payload.cat_id );
    state.projectCategorySetup.data[catIndex].grouping = null;
  },


  //------------------------------------------------------------------

  //PROJECT SETUP - TAG COUNTS CATEGORY-------------------------------
  UPDATE_CATEGORY_TAG_COUNTS_BUSY(state, payload) {
    state.projectSetup.isBusy = payload;
  },
  UPDATE_CATEGORY_TAG_COUNTS_SUCCESS(state, payload) {

    const catIndex = state.projectCategorySetup.data.findIndex(c => c.id == payload.cat_id);
    const tagIndex = state.projectCategorySetup.data[catIndex].tag_count.findIndex(t => t.id == payload.id);

    state.projectCategorySetup.data[catIndex].tag_count[tagIndex] = payload;
    state.projectSetup.error = null;
  },
  UPDATE_CATEGORY_TAG_COUNTS_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
    const catIndex = state.projectCategorySetup.data?.findIndex( c => c.id == payload.cat_id );
    state.projectCategorySetup.data[catIndex].tag_count = null;
  },
  CREATE_CATEGORY_TAG_COUNTS_BUSY(state, payload) {
    state.projectSetup.isBusy = payload;
  },
  CREATE_CATEGORY_TAG_COUNTS_SUCCESS(state, payload) {
    const categoryIndex = state.projectCategorySetup.data.findIndex(
      c => c.id == payload[0].cat_id
    );

    let category = state.projectCategorySetup.data[categoryIndex];
    category.tag_count.push(
      payload[0]
    );

    state.projectCategorySetup.data[categoryIndex] = category;
    state.projectSetup.error = null;
  },
  CREATE_CATEGORY_TAG_COUNTS_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
    const catIndex = state.projectCategorySetup.data?.findIndex( c => c.id == payload.cat_id );
    state.projectCategorySetup.data[catIndex].tag_count = null;
  },
  DELETE_CATEGORY_TAG_COUNTS_BUSY(state, payload) {
    state.projectSetup.isBusy = payload;
  },
  DELETE_CATEGORY_TAG_COUNTS_SUCCESS(state, payload) {

    let categories = [...state.projectCategorySetup.data]

    const catIndex = categories.findIndex(c => c.id == payload.cat_id);
    const tagIndex = categories[catIndex].tag_count.findIndex(t => t.id == payload.id);

    categories[catIndex].tag_count.splice(tagIndex, 1);

    state.projectCategorySetup.data = categories;
    state.projectSetup.error = null;
  },
  DELETE_CATEGORY_TAG_COUNTS_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
    const catIndex = state.projectCategorySetup.data?.findIndex( c => c.id == payload.cat_id );
    state.projectCategorySetup.data[catIndex].tag_count = null;
  },
  //------------------------------------------------------------------

  ROLLBACK_PROJECT_SETUP: (state) => {
    state.projectSetup.data = state.projectSetup.backup;
  },

  IMPORT_OUTLET_BUSY: (state, payload) => {
    state.projectSetup.isImportOutletsBusy = payload;
  },
  IMPORT_OUTLET_SUCCESS: (state, payload) => {
    state.projectSetup.importedOutlets =  payload;
  },
  IMPORT_OUTLET_FAIL: (state, payload) => {
    state.projectSetup.error = payload;
    state.projectSetup.importedOutlets = null;
  }
};

const actions = {
  getprojectSetup: ({ commit, rootState }, { projectUid }) => {
    commit("LOAD_PROJECT_SETUP_BUSY", true);

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    return Axios.get(`${API_HOST}/analysis/projects/${projectUid}/setup`, config)
      .then(response => {
        commit("LOAD_PROJECT_SETUP_SUCCESS", response.data);
        // fill the project setup categories from project setup data if its organizations_with_category falsey
        // so we can use always 1 action func
        if(!response.data?.general?.organizations_with_category) {
          commit("LOAD_PROJECT_CATEGORIES_SETUP_SUCCESS", { data: response.data?.categories, isCategoryExists: response.data?.general?.organizations_with_category });
          commit("LOAD_PROJECT_HIERARCHY_SUCCESS", { data: response.data?.tag_hierarchies });
          commit("LOAD_PROJECT_ASSOCIATION_SUCCESS", { data: response.data?.tag_associations });
        }
        return response.data;
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("LOAD_PROJECT_SETUP_FAIL", error);
      })
      .finally(() => commit("LOAD_PROJECT_SETUP_BUSY", false));
  },
  getProjectCategoriesSetup: ({ commit, rootState }, { projectUid, pagination, filters, reset_state_tags }) => {
    commit("LOAD_PROJECT_CATEGORIES_SETUP_BUSY", true);
    const query = getUrlParams({ ...pagination, search: filters });

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    return Axios.get(`${API_HOST}/analysis/projects/${projectUid}/categories?${query}`, config)
      .then(response => {
        const responseData = response.data.data;
        responseData.reset_state_tags = reset_state_tags
        responseData.isCategoryExists = true;
        commit("LOAD_PROJECT_CATEGORIES_SETUP_SUCCESS", responseData);
        return response.data.data.data;
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("LOAD_PROJECT_CATEGORIES_SETUP_FAIL", error);
      })
      .finally(() => commit("LOAD_PROJECT_CATEGORIES_SETUP_BUSY", false));
  },
  getProjectHierarchy: ({ commit, rootState }, { projectUid, pagination, filters, reset_state_tags }) => {
    commit("LOAD_PROJECT_HIERARCHY_BUSY", true);
    const query = getUrlParams({ ...pagination, search: filters });

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    return Axios.get(`${API_HOST}/analysis/projects/${projectUid}/hierarchies?${query}`, config)
      .then(response => {
        const responseData = response.data.data;
        responseData.reset_state_tags = reset_state_tags
        commit("LOAD_PROJECT_HIERARCHY_SUCCESS", responseData);
        return response.data.data;
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("LOAD_PROJECT_HIERARCHY_FAIL", error);
      })
      .finally(() => commit("LOAD_PROJECT_HIERARCHY_BUSY", false));
  },
  getProjectAssociation: ({ commit, rootState }, { projectUid, pagination, filters, reset_state_tags }) => {
    commit("LOAD_PROJECT_ASSOCIATION_BUSY", true);
    const query = getUrlParams({ ...pagination, search: filters });

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    return Axios.get(`${API_HOST}/analysis/projects/${projectUid}/associations?${query}`, config)
      .then(response => {
        const responseData = response.data.data;
        responseData.reset_state_tags = reset_state_tags
        commit("LOAD_PROJECT_ASSOCIATION_SUCCESS", responseData);
        return response.data.data;
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("LOAD_PROJECT_ASSOCIATION_FAIL", error);
      })
      .finally(() => commit("LOAD_PROJECT_ASSOCIATION_BUSY", false));
  },
  
  exportProjectAssociations: ({ rootState }, { projectUid, ...filters }) => {
    const { accessToken } = rootState.user.user.data;
    const query = getUrlParams({search: filters});
    
    const url = `${API_HOST}/analysis/projects/${projectUid}/associations/export/xlsx?${query}`;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` },
      responseType: 'blob'
    };
    
    return Axios.get(url, config)
      .then(response => {
        // Create blob URL and trigger download
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `associations-export-${new Date().toISOString().split('T')[0]}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        Vue.notify({
          group: "store_notifications",
          type: "success",
          text: i18n.t("tag.messages.export_associations.success")
        });
        
        return response;
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("tag.messages.export_associations.error")
        });
        
        console.error('Export error:', error);
        throw error;
      });
  },
  
  updateProjectSetup: ({ commit, rootState }, { projectUid, payload }) => {
    commit("UPDATE_PROJECT_SETUP_BUSY", true);

    // Cancel the previous request if there is one
    if (state.projectSetup?.abortController) {
      state.projectSetup.abortController?.cancel('Another request triggered');
      commit("CLEAR_PROJECT_SETUP_ABORT_CONTROLLER");
    }

    // Create a new AbortController/cancelToken instance for the current request
    // TODO: "remove this comment after update axios" -> (current old! axios version not supporting the AbortController api, they start to support it from v0.22.0) 
    const CancelToken = Axios.CancelToken;
    const controller = CancelToken.source();
    commit("SET_PROJECT_SETUP_ABORT_CONTROLLER", controller);

    const body = {
      ...payload
    };

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` },
      cancelToken: controller.token
    };
    return new Promise((resolve, reject) => {
      Axios.put(
        `${API_HOST}/analysis/projects/${projectUid}/update`,
        body,
        config
      )
        .then(response => {
          commit("UPDATE_PROJECT_SETUP_SUCCESS", response.data.general);
          resolve(response.data)
        })
        .catch(error => {
          if (Axios.isCancel(error)) {
            console.log('Request canceled => ', error.message);
          } else {
            Vue.notify({
              group: "store_notifications",
              type: "error",
              text: i18n.t("messages.request.error")
            });
            console.error(error);
            commit("UPDATE_PROJECT_SETUP_FAIL", error);
            reject(error);
          }
        })
        .finally(() => commit("UPDATE_PROJECT_SETUP_BUSY", false));
    })
  },
  createProjectSetup: ({ commit }, value) => {
    commit("CREATE_PROJECT_SETUP_BUSY", true);

    // Axios.put(res => {})
    // if success: commit("UPDATE_PROJECT_SETUP_SUCCESS", payload);
    // else: commit("updateProjectSetupError", error);

    const payload = value;

    commit("CREATE_PROJECT_SETUP_SUCCESS", payload);
  },
  deleteProjectSetup: ({ commit }, value) => {
    commit("DELETE_PROJECT_SETUP_BUSY", true);

    // Axios.delete(res => {})
    // if success: commit("UPDATE_PROJECT_SETUP_SUCCESS", payload);
    // else: commit("updateProjectSetupError", error);

    const payload = value;

    commit("DELETE_PROJECT_SETUP_SUCCESS", payload);
  },

  updateTag: ({ commit, rootState }, { projectUid, row }) => {
    commit("UPDATE_TAG_BUSY", true);
    commit("UPDATE_TAG_PRE_PROCESS", row);

    const body = {
      ...row,
      tagId: row.id
    };

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    return Axios.put(
      `${API_HOST}/analysis/${projectUid}/tags/${row.cat_id}/update`,
      body,
      config
    )
      .then(response => {
        if (!response.data.error) {
          commit("UPDATE_TAG_SUCCESS", response.data);
        }else{
          Vue.notify({
            group: "store_notifications",
            type: "error",
            text: i18n.t(`tag.form.errors.default.${response.data.error}`)
          });
        }
        return response.data;
      })
      .catch(error => {
        const errorKey = error?.response?.data?.message;
        const i18nKey = errorKey ? `tag.form.errors.default.${errorKey}` : null;
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18nKey && i18n.te(i18nKey) ? i18n.t(i18nKey) : i18n.t("messages.request.error")
        });

        console.error(error);
        commit("UPDATE_TAG_FAIL", { ...(error?.response?.data || error), cat_id: row.cat_id });
        return Promise.reject(error);
      })
      .finally(() => commit("UPDATE_TAG_BUSY", false));
  },

  updateTagCategory: ({ commit, rootState }, {
    newCategoryId,
    categoryId,
    tagId,
    projectId
  }) => {
    commit("UPDATE_TAG_BUSY", true);

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    return Axios.put(
      `${API_HOST}/analysis/${projectId}/tags/${categoryId}/update/category`,
      { tag_id: tagId, category_id: newCategoryId},
      config
    )
      .then((response) => {
        commit("UPDATE_TAG_CATEGORY", { newCategoryId, tagId, categoryId, tagKey: response.data.data.tag_key });
        Vue.notify({
          group: "store_notifications",
          type: "success",
          text: i18n.t(`tag.form.success.category_updated`)
        });
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("ROLLBACK_PROJECT_SETUP", true);
        commit("UPDATE_TAG_FAIL", error);
      })
      .finally(() => commit("UPDATE_TAG_BUSY", false));
  },

  createTag: ({ commit, rootState }, { projectUid, row }) => {
    commit("CREATE_TAG_BUSY", true);

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    const body = {
      ...row
    }

    return Axios.post(
      `${API_HOST}/analysis/${projectUid}/tags/${row.cat_id}/create`, body, config
    )
      .then(response => {
        if (!response.data.error) {
          commit("CREATE_TAG_SUCCESS", response.data);
        }
        return response.data
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("CREATE_TAG_FAIL", error);
      })
      .finally(() => commit("CREATE_TAG_BUSY", false));
  },
  deleteTag: ({ commit, rootState }, { projectUid, row }) => {
    // commit("DELETE_TAG_BUSY", true);
    const body = {
      tagId: [row.id]
    };
    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` },
      data: body
    };
    Axios.delete(
      `${API_HOST}/analysis/${projectUid}/tags/${row.cat_id}/delete`,
      config
    )
      .then(() => {
        commit("DELETE_TAG_SUCCESS", row);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("DELETE_TAG_FAIL", error);
      })
      .finally(() => commit("DELETE_TAG_BUSY", false));
  },

  updateGeneral: ({ commit, rootState }, { projectUid, payload }) => {
    commit("UPDATE_GENERAL_BUSY", true);

    const body = {
      ...payload
    };

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    Axios.put(
      `${API_HOST}/analysis/projects/${projectUid}/general/update`,
      body,
      config
    )
      .then(response => {
        commit("UPDATE_GENERAL_SUCCESS", response.data.general);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });
        console.error(error);
        commit("UPDATE_GENERAL_FAIL", error);
      })
      .finally(() => commit("UPDATE_GENERAL_BUSY", false));
  },

  getCategories: ({ commit, rootState }, { projectUid, pagination, search}) => {
    commit("LOAD_CATEGORIES_BUSY", true);
    commit("LOAD_CATEGORIES_IS_READY", false);
    const normalizedPagination = normalize(pagination);

    const query = getUrlParams({ ...pagination, search });

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };
    return new Promise((resolve, reject) => {
      Axios.get(`${API_HOST}/analysis/${projectUid}/categories?${query}`, config)
        .then(response => {
          commit("LOAD_CATEGORIES_SUCCESS", response.data);
          resolve(response.data.data)
        })
        .catch(error => {
          Vue.notify({
            group: "store_notifications",
            type: "error",
            text: i18n.t("messages.request.error")
          });

          console.error(error);
          commit("LOAD_CATEGORIES_FAIL", error);
          reject()
        })
        .finally(() => {
          commit("LOAD_CATEGORIES_IS_READY", true);
          commit("LOAD_CATEGORIES_BUSY", false);
        });
    })
  },
  updateCategories: ({ commit, rootState }, { projectUid, row }) => {
    commit("UPDATE_CATEGORIES_BUSY", true);

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    const body = { ...row };

    Axios.put(
      `${API_HOST}/analysis/${projectUid}/categories/${row.id}/update`,
      body,
      config
    )
      .then(response => {
        if (!response.data.error) {
          commit("UPDATE_CATEGORIES_SUCCESS", response.data);
        }
        else {
          Vue.notify({
            group: "store_notifications",
            type: "error",
            text: i18n.t(`category.form.errors.${response.data.error}`)
          });
        }
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("UPDATE_CATEGORIES_FAIL", error);
      })
      .finally(() => commit("UPDATE_CATEGORIES_BUSY", false));
  },
  createCategories: ({ commit, rootState }, { projectUid, category }) => {
    commit("CREATE_CATEGORIES_BUSY", true);

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    return Axios.post(`${API_HOST}/analysis/${projectUid}/categories/create`, category, config)
      .then(response => {
        if (!response.data.error) {
          commit("CREATE_CATEGORIES_SUCCESS", response.data);
        }
        return response.data;
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("CREATE_CATEGORIES_FAIL", error);
      })
      .finally(() => commit("CREATE_CATEGORIES_BUSY", false));
  },
  deleteCategories: ({ commit, rootState }, { projectUid, row }) => {
    commit("DELETE_CATEGORIES_BUSY", true);

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    Axios.delete(
      `${API_HOST}/analysis/${projectUid}/categories/${row.id}/delete`,
      config
    )
      .then((response) => {
        commit("DELETE_CATEGORIES_SUCCESS", response.data.id);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("DELETE_CATEGORIES_FAIL", error);
      })
      .finally(() => commit("DELETE_CATEGORIES_BUSY", false));
  },
  updateScoring: ({ commit, rootState }, { projectUid, payload }) => {
    commit("UPDATE_SCORING_BUSY", true);
    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    const body = {
      ...payload
    };

    Axios.put(
      `${API_HOST}/analysis/projects/${projectUid}/scoring/update`,
      body,
      config
    )
      .then(response => {
        commit("UPDATE_SCORING_SUCCESS", response.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("UPDATE_SCORING_FAIL", error);
      })
      .finally(() => commit("UPDATE_SCORING_BUSY", false));
  },

  addRelationship: (
    { commit, rootState },
    { projectUid, type, relationship }
  ) => {
    commit("ADD_RELATIONSHIP_BUSY", true);

    const { accessToken } = rootState.user.user.data;

    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    const body = {
      ...relationship
    };

    return new Promise((resolve, reject) => {
      Axios.post(
        `${API_HOST}/analysis/${projectUid}/relationships/${type}`,
        body,
        config
      )
        .then(
          response => {
            commit("ADD_OR_UPDATE_RELATIONSHIP_SUCCESS", { type, data: response.data });
            resolve(response.data);
          },
          error => {
            commit("ADD_RELATIONSHIP_FAIL", error);
            reject(error);
          }
        )
        .catch(error => {
          Vue.notify({
            group: "store_notifications",
            type: "error",
            text: i18n.t("messages.request.error")
          });

          console.error(error);
          commit("ADD_RELATIONSHIP_FAIL", error);
        })
        .finally(() => commit("ADD_RELATIONSHIP_BUSY", false));
    });
  },

  updateRelationship: (
    { commit, rootState },
    { projectUid, type, relationship }
  ) => {
    commit("UPDATE_RELATIONSHIP_BUSY", true);

    const { accessToken } = rootState.user.user.data;

    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    const body = {
      ...relationship
    };

    return new Promise((resolve, reject) => {
      Axios.put(
        `${API_HOST}/analysis/${projectUid}/relationships/${type}`,
        body,
        config
      )
        .then(
          response => {
            commit("ADD_OR_UPDATE_RELATIONSHIP_SUCCESS", {
              type,
              data: response.data
            });
            resolve(response.data);
          },
          error => {
            commit("UPDATE_RELATIONSHIP_FAIL", error);
            reject(error);
          }
        )
        .catch(error => {
          Vue.notify({
            group: "store_notifications",
            type: "error",
            text: i18n.t("messages.request.error")
          });

          console.error(error);
          commit("UPDATE_RELATIONSHIP_FAIL", error);
        })
        .finally(() => commit("UPDATE_RELATIONSHIP_BUSY", false));
    });
  },

  removeRelationship: (
    { commit, rootState },
    { projectUid, type, relationship }
  ) => {
    commit("REMOVE_RELATIONSHIP_BUSY", true);

    const { accessToken } = rootState.user.user.data;

    const config = {
      headers: { Authorization: `Token token=${accessToken}` },
      data: relationship
    };

    return new Promise((resolve, reject) => {
      Axios.delete(
        `${API_HOST}/analysis/${projectUid}/relationships/${type}`,
        config
      )
        .then(
          () => {
            commit("REMOVE_RELATIONSHIP_SUCCESS", { relationship, type });
            resolve(relationship);
          },
          error => {
            commit("REMOVE_RELATIONSHIP_FAIL", error);
            reject(error);
          }
        )
        .catch(error => {
          Vue.notify({
            group: "store_notifications",
            type: "error",
            text: i18n.t("messages.request.error")
          });

          console.error(error);
          commit("REMOVE_RELATIONSHIP_FAIL", error);
        })
        .finally(() => commit("REMOVE_RELATIONSHIP_BUSY", false));
    });
  },

  updateGrouping: ({ commit, rootState }, { projectUid, grouping }) => {
    commit("UPDATE_GROUPING_BUSY", true);

    const { accessToken } = rootState.user.user.data;

    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    const body = {
      ...grouping
    };

    return new Promise((resolve, reject) => {
      Axios.put(
        `${API_HOST}/analysis/${projectUid}/groupings/${grouping.group_id}/tags`,
        body,
        config
      )
        .then(
          response => {
            commit("UPDATE_GROUPING_SUCCESS", response.data);
            resolve(response.data);
          },
          error => {
            commit("UPDATE_GROUPING_FAIL", error);
            reject(error);
          }
        )
        .catch(error => {
          Vue.notify({
            group: "store_notifications",
            type: "error",
            text: i18n.t("messages.request.error")
          });

          console.error(error);
          commit("UPDATE_GROUPING_FAIL", error);
        })
        .finally(() => commit("UPDATE_GROUPING_BUSY", false));
    });
  },

  createSource: ({ commit, rootState }, { projectUid, row }) => {
    commit("CREATE_SOURCE_BUSY", true);

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    Axios.post(
      `${API_HOST}/analysis/${projectUid}/sources/${row.cat_id}/create`,
      config
    )
      .then(response => {
        commit("CREATE_SOURCE_SUCCESS", response.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("CREATE_SOURCE_FAIL", error);
      })
      .finally(() => commit("CREATE_SOURCE_BUSY", false));
  },

  getSources: ({ commit, rootState }, { projectUid, pagination, filters = {} }) => {
    commit('LOAD_SOURCES_BUSY', true);
    const { accessToken } = rootState.user.user.data;
    let route = `${API_HOST}/analysis/${projectUid}/sources`;

    const query = getUrlParams({ ...pagination, search: filters });

    if (query) route += `?${query}`;

    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    return new Promise((resolve, reject) => {
      Axios.get(route, config)
        .then(
          response => {
            const data = response.data.status ? response.data.data : response.data
            commit("LOAD_SOURCES_SUCCESS", data);
            resolve(response.data);
          },
          error => {
            commit("LOAD_SOURCES_FAIL", error);
            reject(error);
          }
        )
        .catch(error => {
          Vue.notify({
            group: "store_notifications",
            type: "error",
            text: i18n.t("messages.request.error")
          });

          console.error(error);
          commit("LOAD_SOURCES_FAIL", error);
        })
        .finally(() => commit("LOAD_SOURCES_BUSY", false));
    });
  },

  getCategorySources: ({ commit, rootState }, { projectUid, categoryId, pagination, filters = null }) => {
    commit('LOAD_CATEGORY_TAGS_BUSY', true);
    commit("LOAD_CATEGORY_TAGS_IS_READY", false);
    if (pagination.page === 1){
      commit('LOAD_CATEGORIES_BUSY', true);
      commit("CREATE_CATEGORY_TAGS", null);
    }

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    let query = "?";
    const normalizedPagination = normalize(pagination);
    Object.keys(normalizedPagination).map((param, i) => {
      query += `${param}=${normalizedPagination[param]}${i !== Object.keys(normalizedPagination).length - 1 ? '&' : ''}`;
    });
    if(filters){ 
      query += `&${getUrlParams({ search: filters })}`; 
    }

    const url = `${API_HOST}/analysis/${projectUid}/sources/${categoryId}${query}`;

    return Axios.get(url, config)
      .then(response => {
        const sources = response.data.data
        commit("LOAD_CATEGORY_TAGS_SUCCESS", {tags: sources.data, pagination: sources.pagination} )
        return sources.data;
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });
        console.error(error);
        commit("LOAD_CATEGORY_TAGS_FAIL", error)
      })
      .finally(() => {
        commit("LOAD_CATEGORY_TAGS_IS_READY", true);
        commit("LOAD_CATEGORY_TAGS_BUSY", false);
        if(pagination.page === 1){
          commit('LOAD_CATEGORIES_BUSY', false);
        }
      });
  },

  addCategorySource: ({ commit, rootState }, { projectUid, source, pagination }) => {
    commit("CREATE_TAG_BUSY", true);

    const { accessToken } = rootState.user.user.data;

    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    const body = {
      ...source
    };

    //return new Promise((resolve, reject) => {
    return Axios.post(
        `${API_HOST}/analysis/${projectUid}/sources/create`,
        body,
        config
      )
      .then(response => {
        if (!response.data.error) {
          commit("CREATE_TAG_SUCCESS", response.data);
        }
        return response.data;
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("CREATE_TAG_FAIL", error);
      })
      .finally(() => commit("CREATE_TAG_BUSY", false));
  },
  removeCategorySource: ({ commit, rootState }, { projectUid, sourceId }) => {
    commit("DELETE_TAG_BUSY", true);

    const { accessToken } = rootState.user.user.data;

    const config = {
      headers: { Authorization: `Token token=${accessToken}` },
      data: { source_id: sourceId }
    };

    return new Promise((resolve, reject) => {
      Axios.delete(`${API_HOST}/analysis/${projectUid}/sources/delete`, config)
        .then(
          response => {

            commit("DELETE_TAG_SUCCESS", response.data);
            resolve(response.data);
          },
          error => {
            commit("DELETE_TAG_FAIL", error);
            reject(error);
          }
        )
        .catch(error => {
          Vue.notify({
            group: "store_notifications",
            type: "error",
            text: i18n.t("messages.request.error")
          });

          console.error(error);
          commit("DELETE_TAG_FAIL", error);
        })
        .finally(() => commit("DELETE_TAG_BUSY", false));
    });
  },

  addSource: ({ commit, rootState }, { projectUid, source }) => {
    commit("ADD_SOURCE_BUSY", true);

    const { accessToken } = rootState.user.user.data;

    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    const body = {
      ...source
    };

    return Axios.post(
      `${API_HOST}/analysis/${projectUid}/sources/create`,
        body,
        config
      )
      .then(response => {

        if (!response.data.error) {
          commit("ADD_SOURCE_SUCCESS", response.data);
        }
        return response.data;
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("ADD_SOURCE_FAIL", error);
      })
      .finally(() => commit("ADD_SOURCE_BUSY", false));
  },

  updateSource: ({ commit, rootState }, { projectUid, source }) => {
    commit("UPDATE_SOURCE_BUSY", true);

    const { accessToken } = rootState.user.user.data;

    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    const body = {
      ...source,
      source_id: source.id
    };

    //return new Promise((resolve, reject) => {
    return Axios.put(
        `${API_HOST}/analysis/${projectUid}/sources/update`,
        body,
        config
      )
        .then(
          response => {
            if (!response.data.error) {
              commit("UPDATE_SOURCE_SUCCESS", response.data);
            }else{
              Vue.notify({
                group: "store_notifications",
                type: "error",
                text: i18n.t(`tag.form.errors.source.${response.data.error}`)
              });
            }
            return response.data;
          }
        )
        .catch(error => {
          Vue.notify({
            group: "store_notifications",
            type: "error",
            text: i18n.t("messages.request.error")
          });

          console.error(error);
          commit("UPDATE_SOURCE_FAIL", error);
        })
        .finally(() => commit("UPDATE_SOURCE_BUSY", false));
    //});
  },

  updateCategorySource: ({ commit, rootState }, { projectUid, source }) => {
    commit("UPDATE_TAG_BUSY", true);

    const { accessToken } = rootState.user.user.data;

    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    const body = {
      ...source,
      source_id: source.id
    };

    Axios.put(
      `${API_HOST}/analysis/${projectUid}/sources/update`,
      body,
      config
    )
      .then(
        response => {
          if (!response.data.error) {
            commit("UPDATE_TAG_SUCCESS", response.data);
          }else{
            Vue.notify({
              group: "store_notifications",
              type: "error",
              text: i18n.t(`tag.form.errors.source.${response.data.error}`)
            });
          }
          return response.data;
        }
      )
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("UPDATE_TAG_FAIL", error);
      })
      .finally(() => commit("UPDATE_TAG_BUSY", false));
  },

  removeSource: ({ commit, rootState }, { projectUid, sourceId }) => {
    commit("REMOVE_SOURCE_BUSY", true);

    const { accessToken } = rootState.user.user.data;

    const config = {
      headers: { Authorization: `Token token=${accessToken}` },
      data: { source_id: sourceId }
    };

    return new Promise((resolve, reject) => {
      Axios.delete(`${API_HOST}/analysis/${projectUid}/sources/delete`, config)
        .then(
          response => {
            commit("REMOVE_SOURCE_SUCCESS", sourceId);
            resolve(response.data);
          },
          error => {
            commit("REMOVE_SOURCE_FAIL", error);
            reject(error);
          }
        )
        .catch(error => {
          Vue.notify({
            group: "store_notifications",
            type: "error",
            text: i18n.t("messages.request.error")
          });

          console.error(error);
          commit("REMOVE_SOURCE_FAIL", error);
        })
        .finally(() => commit("REMOVE_SOURCE_BUSY", false));
    });
  },

  getSourcesAffiliations: ({ commit, rootState }, { projectUid }) => {
    commit("LOAD_SOURCES_AFFILIATIONS_BUSY", true);

    const { accessToken } = rootState.user.user.data;

    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    return new Promise((resolve, reject) => {
      Axios.get(`${API_HOST}/analysis/${projectUid}/affiliations`, config)
        .then(
          response => {
            commit("LOAD_SOURCES_AFFILIATIONS_SUCCESS", response.data);
            resolve(response.data);
          },
          error => {
            commit("LOAD_SOURCES_AFFILIATIONS_FAIL", error);
            reject(error);
          }
        )
        .catch(error => {
          Vue.notify({
            group: "store_notifications",
            type: "error",
            text: i18n.t("messages.request.error")
          });

          console.error(error);
          commit("LOAD_SOURCES_AFFILIATIONS_FAIL", error);
        })
        .finally(() => commit("LOAD_SOURCES_AFFILIATIONS_BUSY", false));
    });
  },
  deleteSource: ({ commit, rootState }, { projectUid, row }) => {
    commit("DELETE_SOURCE_BUSY", true);

    const body = {
      sourceId: [row.id]
    };

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` },
      data: body
    };

    Axios.delete(
      `${API_HOST}/analysis/${projectUid}/sources/${row.cat_id}/delete`,
      config
    )
      .then(() => {
        commit("DELETE_SOURCE_SUCCESS", row);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("DELETE_SOURCE_FAIL", error);
      })
      .finally(() => commit("DELETE_SOURCE_BUSY", false));
  },

  getGrouping: ({ commit, rootState }, { projectUid , source, pagination}) => {
    commit("LOAD_CATEGORY_GROUPING_BUSY", true);
    commit("LOAD_CATEGORY_GROUPING_IS_READY", false);

    const { accessToken } = rootState.user.user.data;

    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    let query = "?";
    const normalizedPagination = normalize(pagination);

    Object.keys(normalizedPagination).map((param, i) => {
      query += `${param}=${normalizedPagination[param]}${i !== Object.keys(normalizedPagination).length - 1 ? '&' : ''}`;
    });


    return new Promise((resolve, reject) => {
      Axios.get(`${API_HOST}/analysis/${projectUid}/groupings/${source.cat_id}${query}`, config)
        .then(
          response => {
            commit("LOAD_CATEGORY_GROUPING_SUCCESS", response.data);
            resolve(response.data);
          },
          error => {
            commit("LOAD_CATEGORY_GROUPING_FAIL", error);
            reject(error);
          }
        )
        .catch(error => {
          Vue.notify({
            group: "store_notifications",
            type: "error",
            text: i18n.t("messages.request.error")
          });

          console.error(error);
          commit("LOAD_CATEGORY_GROUPING_FAIL", error);
        })
        .finally(() => {
          commit("LOAD_CATEGORY_GROUPING_IS_READY", true);
          commit("LOAD_CATEGORY_GROUPING_BUSY", false)
        });
    });
  },

  updateCategoryGrouping: ({ commit, rootState }, { projectUid, row }) => {
    commit("UPDATE_CATEGORY_GROUPING_BUSY", true);

    const body = {
      ...row,
      grouping_id: row.id
    };

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    return Axios.put(
      `${API_HOST}/analysis/${projectUid}/groupings/update`,
      body,
      config
    )
      .then(response => {
        if (!response.data.error) {
          commit("UPDATE_CATEGORY_GROUPING_SUCCESS", response.data);
        }else{
          Vue.notify({
            group: "store_notifications",
            type: "error",
            text: i18n.t(`tag.form.errors.grouping.${response.data.error}`)
          });
        }
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("UPDATE_CATEGORY_GROUPING_FAIL", error);
      })
      .finally(() => commit("UPDATE_CATEGORY_GROUPING_BUSY", false));
  },

  createCategoryGrouping: (
    { commit, rootState },
    { projectUid, row }
    ) => {
    commit("CREATE_CATEGORY_GROUPING_BUSY", true);

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    const body = {
      ...row
    }

    return Axios.post(
      `${API_HOST}/analysis/${projectUid}/groupings/${row.cat_id}/create`,
      body,
      config
    )
      .then(response => {
        if (!response.data.error) {
          commit("CREATE_CATEGORY_GROUPING_SUCCESS", response.data);
        }
        return response.data
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("CREATE_CATEGORY_GROUPING_FAIL", error);
      })
      .finally(() => commit("CREATE_CATEGORY_GROUPING_BUSY", false));
  },
  deleteCategoryGrouping: ({ commit, state, rootState }, { projectUid, row }) => {
    commit("DELETE_CATEGORY_GROUPING_BUSY", true);

    const body = {
      grouping_id: row.id
    };

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` },
      data: body
    };

    Axios.delete(
      `${API_HOST}/analysis/${projectUid}/groupings/delete`,
      config
    )
      .then(response => {
        commit("DELETE_CATEGORY_GROUPING_SUCCESS", response.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("DELETE_CATEGORY_GROUPING_FAIL", error);
      })
      .finally(() => commit("DELETE_CATEGORY_GROUPING_BUSY", false));
  },
  updateTagCount: ({ commit, rootState }, { projectUid, row }) => {

    commit("UPDATE_CATEGORY_TAG_COUNTS_BUSY", true);

    const body = {
      ...row,
    };

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    Axios.put(
      `${API_HOST}/analysis/${projectUid}/categories/${row.cat_id}/tag_count/update`,
      body,
      config
    )
      .then(() => {
        commit("UPDATE_CATEGORY_TAG_COUNTS_SUCCESS", row);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("UPDATE_CATEGORY_TAG_COUNTS_FAIL", error);
      })
      .finally(() => commit("UPDATE_CATEGORY_TAG_COUNTS_BUSY", false));
  },
  createTagCount: (
    { commit, rootState },
    { projectUid, row }
  ) => {
    commit("CREATE_CATEGORY_TAG_COUNTS_BUSY", true);

    const body = {
      ...row
    };

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    Axios.post(
      `${API_HOST}/analysis/${projectUid}/categories/${row.cat_id}/tag_count/create`,
      body,
      config
    )
      .then(response => {
        commit("CREATE_CATEGORY_TAG_COUNTS_SUCCESS", response.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("CREATE_CATEGORY_TAG_COUNTS_FAIL", error);
      })
      .finally(() => commit("CREATE_CATEGORY_TAG_COUNTS_BUSY", false));
  },
  saveTagCountInDb: (
    { commit, rootState },
    { projectUid, rows, deletedRow, cat_id }
  ) => {
    commit("BULK_UPDATE_CATEGORY_TAG_COUNTS_BUSY", true);

    const body = {
      tag_counts: rows,
      tag_counts_to_delete: deletedRow
    };

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    Axios.post(
      `${API_HOST}/analysis/${projectUid}/categories/${cat_id}/tag_count/bulk_update`,
      body,
      config
    )
      .then(response => {
        commit("BULK_UPDATE_CATEGORY_TAG_COUNTS_SUCCESS", response.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("BULK_UPDATE_CATEGORY_TAG_COUNTS_FAIL", error);
      })
      .finally(() => commit("BULK_UPDATE_CATEGORY_TAG_COUNTS_BUSY", false));
  },

  deleteTagCount: (
    { commit, rootState },
    { projectUid, row }
  ) => {
    commit("DELETE_CATEGORY_TAG_COUNTS_BUSY", true);

    const body = {
      ...row
    };

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` },
      data: body
    };

    Axios.delete(
      `${API_HOST}/analysis/${projectUid}/categories/${row.cat_id}/tag_count/delete`,
      config
    )
      .then(() => {

        commit("DELETE_CATEGORY_TAG_COUNTS_SUCCESS", row);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("DELETE_CATEGORY_TAG_COUNTS_FAIL", error);
      })
      .finally(() => commit("DELETE_CATEGORY_TAG_COUNTS_BUSY", false));
  },
  getCategoryTags: ({ commit, rootState }, { projectUid, categoryId, pagination, filters = null }) => {
    commit('LOAD_CATEGORY_TAGS_BUSY', true);
    commit('LOAD_CATEGORY_TAGS_IS_READY', false);
    if (pagination.page === 1){
      commit('CREATE_CATEGORY_TAGS', null);
      commit('LOAD_CATEGORIES_BUSY', true);
    }

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    let query = "?";
    const normalizedPagination = normalize(pagination);
    Object.keys(normalizedPagination).map((param, i) => {
      query += `${param}=${normalizedPagination[param]}${i !== Object.keys(normalizedPagination).length - 1 ? '&' : ''}`;
    });
    if (filters && Object.keys(filters).length > 0){ 
      query += `&${getUrlParams( filters )}`;
    } else if(filters){
      query += `&${getUrlParams({ search: filters })}`;
    }

    const url = `${API_HOST}/analysis/${projectUid}/tags/${categoryId}${query}`;

    return Axios.get(url, config)
      .then(response => {
        const tags = response.data.data;
        commit('LOAD_CATEGORY_TAGS_SUCCESS', { tags: tags.data, pagination: tags.pagination });
        return tags.data;
      })
      .catch(error => {
        Vue.notify({
          group: 'store_notifications',
          type: 'error',
          text: i18n.t('messages.request.error')});

        console.error(error);
        commit('LOAD_CATEGORY_TAGS_FAIL', error);
      })
      .finally(() => {
        commit('LOAD_CATEGORY_TAGS_IS_READY', true);
        commit('LOAD_CATEGORY_TAGS_BUSY', false);
        if(pagination.page === 1){
          commit('LOAD_CATEGORIES_BUSY', false);
        }
      });
  },
  getCategoryTagsSearch: ({ commit, rootState }, { projectUid, pagination, filters = null }) => {
    commit('LOAD_CATEGORY_TAGS_BUSY', true);
    commit('LOAD_CATEGORY_TAGS_IS_READY', false);
    if (pagination.page === 1){
      commit('CREATE_CATEGORY_TAGS', null);
      commit('LOAD_CATEGORIES_BUSY', true);
    }

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    let query = "?";
    const normalizedPagination = normalize(pagination);
    Object.keys(normalizedPagination).map((param, i) => {
      query += `${param}=${normalizedPagination[param]}${i !== Object.keys(normalizedPagination).length - 1 ? '&' : ''}`;
    });
    if(filters){ 
      query += `&${getUrlParams({ search: filters })}`;
    }

    const url = `${API_HOST}/analysis/${projectUid}/categories${query}`;

    Axios.get(url, config)
      .then(response => {
        const tags = response.data.data
        commit('LOAD_CATEGORY_TAGS_SUCCESS', {tags: tags.data, pagination: tags.pagination })
      })
      .catch(error => {
        Vue.notify({
          group: 'store_notifications',
          type: 'error',
          text: i18n.t('messages.request.error')});

        console.error(error);
        commit('LOAD_CATEGORY_TAGS_FAIL', error);
      })
      .finally(() => {
        commit('LOAD_CATEGORY_TAGS_IS_READY', true);
        commit('LOAD_CATEGORY_TAGS_BUSY', false);
        if (pagination.page === 1){
          commit('LOAD_CATEGORIES_BUSY', false);
        }
      });
  },
  getGroupingsWithTags: ({ commit, rootState }, { projectId, pagination }) => {
    commit('LOAD_GROUPINGS_BUSY', true);
    commit('LOAD_GROUPINGS_IS_READY', false);

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    let query = "?";

    if (pagination) {
      const normalizedPagination = normalize(pagination);

      Object.keys(normalizedPagination).map((param, i) => {
        query += `${param}=${normalizedPagination[param]}${i !== Object.keys(normalizedPagination).length - 1 ? '&' : ''}`;
      });
    }

    Axios
      .get(`${API_HOST}/analysis/${projectId}/groupings/groupings${query}`, config)
      .then(response => {
        commit('LOAD_GROUPINGS_SUCCESS', response.data)
      })
      .catch(error => {
        Vue.notify({
          group: 'store_notifications',
          type: 'error',
          text: i18n.t('messages.request.error')});

        console.error(error);
        commit('LOAD_GOUPINGS_FAIL', error)
      })
      .finally(() => {
        commit('LOAD_GROUPINGS_IS_READY', true);
        commit('LOAD_GROUPINGS_BUSY', false)
      });
  },
  updateBulkTagsByProject: ({ commit, rootState }, { projectUid, bulkTags }) => {
    commit("UPDATE_PROJECT_BULK_TAGS_BUSY", true);

    let route = `${API_HOST}/analysis/${projectUid}/tags/bulk/update`

    const body = [
      ...bulkTags,
    ];

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    Axios.put(
      route,
      body,
      config
    )
      .then(response => {
        commit("UPDATE_PROJECT_BULK_TAGS_SUCCESS", response.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("UPDATE_PROJECT_BULK_TAGS_FAIL", error);
      })
      .finally(() => commit("UPDATE_PROJECT_BULK_TAGS_BUSY", false));
  },
  updateBulkSourceByProject: ({ commit, rootState }, { projectUid, bulkTags, pagination }) => {
    commit("UPDATE_PROJECT_BULK_TAGS_BUSY", true);
    const body = [
      ...bulkTags,
    ];

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };
    return new Promise((resolve, reject) => {
      Axios.put(
        `${API_HOST}/analysis/${projectUid}/sources/bulk/update`,
        body,
        config
      )
        .then(
          response => {
            if(pagination) commit("UPDATE_PROJECT_SOURCE_SUCCESS",  response.data)
            else commit("UPDATE_PROJECT_CATEGORIES_BULK_SOURCES_SUCCESS", response.data);
            resolve(response.data)
          },
          error => {
            commit("UPDATE_PROJECT_BULK_TAGS_FAIL", error);
            reject(error);
          }
        )
        .catch(error => {
          Vue.notify({
            group: "store_notifications",
            type: "error",
            text: i18n.t("messages.request.error")
          });

          console.error(error);
          commit("UPDATE_PROJECT_BULK_TAGS_FAIL", error);
        })
        .finally(() => commit("UPDATE_PROJECT_BULK_TAGS_BUSY", false));
    })
  },
  deleteBulkTagsByCategory: ({ commit, rootState }, { projectUid, categoryId, bulkTags }) => {
    commit("DELETE_CATEGORY_BULK_TAGS_BUSY", true);

    const data = [
      ...bulkTags,
    ];

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` },
      data
    };

    return Axios.delete(
      `${API_HOST}/analysis/${projectUid}/tags/${categoryId}/bulk/delete`,
      config
    )
      .then(response => {
        commit("DELETE_CATEGORY_BULK_TAGS_SUCCESS", response.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("DELETE_CATEGORY_BULK_TAGS_FAIL", error);
      })
      .finally(() => commit("DELETE_CATEGORY_BULK_TAGS_BUSY", false));
  },
  deleteBulkGroupingsByCategory: ({ commit, rootState }, { projectUid, categoryId, bulkTags }) => {
    commit("DELETE_CATEGORY_BULK_TAGS_BUSY", true);

    const data = [
      ...bulkTags,
    ];

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` },
      data
    };

    return Axios.delete(
      `${API_HOST}/analysis/${projectUid}/groupings/bulk/delete`,
      config
    )
      .then(response => {
        commit("DELETE_CATEGORY_BULK_TAGS_SUCCESS", response.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("DELETE_CATEGORY_BULK_TAGS_FAIL", error);
      })
      .finally(() => commit("DELETE_CATEGORY_BULK_TAGS_BUSY", false));
  },
  deleteBulkSourcesByCategory: ({ commit, rootState }, { projectUid, categoryId, bulkTags }) => {
    commit("DELETE_CATEGORY_BULK_TAGS_BUSY", true);

    const data = [
      ...bulkTags,
    ];

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` },
      data
    };

    return Axios.delete(
      `${API_HOST}/analysis/${projectUid}/sources/bulk/delete`,
      config
    )
      .then(response => {
        commit("DELETE_CATEGORY_BULK_TAGS_SUCCESS", response.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("DELETE_CATEGORY_BULK_TAGS_FAIL", error);
      })
      .finally(() => commit("DELETE_CATEGORY_BULK_TAGS_BUSY", false));
  },
  deleteBulkTags: ({ commit, rootState }, { projectUid, categoryId, bulkTags }) => {
    commit("DELETE_PROJECT_BULK_TAGS_BUSY", true);

    const data = [
      ...bulkTags,
    ];

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` },
      data
    };

    return Axios.delete(
      `${API_HOST}/analysis/${projectUid}/tags/${categoryId}/bulk/delete`,
      config
    )
      .then(response => {
        commit("DELETE_PROJECT_BULK_TAGS_SUCCESS", response.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("DELETE_PROJECT_BULK_TAGS_FAIL", error);
      })
      .finally(() => commit("DELETE_PROJECT_BULK_TAGS_BUSY", false));
  },
  deleteBulkSources: ({ commit, rootState }, { projectUid, categoryId, bulkTags, pagination }) => {
    commit("DELETE_PROJECT_BULK_TAGS_BUSY", true);

    const data = [
      ...bulkTags,
    ];

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` },
      data
    };

    return Axios.delete(
      `${API_HOST}/analysis/${projectUid}/sources/bulk/delete`,
      config
    )
      .then(response => {
        if(pagination) return commit("DELETE_SOURCES_SUCCESS", response.data)
        commit("DELETE_PROJECT_BULK_TAGS_SUCCESS", response.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("DELETE_PROJECT_BULK_TAGS_FAIL", error);
      })
      .finally(() => commit("DELETE_PROJECT_BULK_TAGS_BUSY", false));
  },
  deleteTagsWithNoAssociatedArticles: ({ commit, rootState }, { projectUid, categoryId, parentTagId }) => {
    commit("DELETE_PROJECT_BULK_TAGS_BUSY", true);

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` },
      params: {
        parent_tag_id: parentTagId
      }
    };

    return Axios.delete(
      `${API_HOST}/analysis/${projectUid}/tags/${categoryId}/delete_tags_with_no_associated_articles`,
      config
    )
      .then(response => {
        commit("DELETE_PROJECT_BULK_TAGS_SUCCESS", response.data);
        Vue.notify({
          group: "store_notifications",
          type: "success",
          text: i18n.t("messages.request.success")
        });
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("DELETE_PROJECT_BULK_TAGS_FAIL", error);
      })
      .finally(() => commit("DELETE_PROJECT_BULK_TAGS_BUSY", false));
  },
  mergeTags: ({ commit, rootState }, {
    projectUid,
    tagId,
    selectedTags,
    parentTagId,
    categoryIsSource = false,
    isOrganization = false
  }) => {
    commit("MERGE_TAGS_BUSY", true);

    let route = `${API_HOST}/analysis/${projectUid}/tags/merge`

    if(categoryIsSource)
      route = `${API_HOST}/analysis/${projectUid}/sources/merge`;

    if(isOrganization)
      route = `${API_HOST}/analysis/${projectUid}/tags/organization/merge`;

    const tags = [
      tagId,
      ...selectedTags
    ];

    const authId = localStorage.getLocalStorage('request_auth_id');
    const body = {
      tags,
      authId,
      parentTagId
    }

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };
    return new Promise((resolve, reject) => {
      Axios.put(
        route,
        body,
        config
      )
        .then(
          response => {
            if(categoryIsSource) {
              const raw = response?.data?.data ?? response?.data;
              const updatePayload = Array.isArray(raw) ? raw[0] : raw;
              const updatedData = {
                update: updatePayload,
                remove: selectedTags.filter(tag => tag.id !== tagId)
              }
              commit("MERGE_SOURCES_SUCCESS", updatedData);
            }
            else {
              const updatedData = {
                // Handled for MMI organization merge
                update: parentTagId && response?.data?.[0] ? response.data[0] : selectedTags.filter(tag => tag.id === tagId),
                remove: selectedTags.filter(tag => tag.id !== tagId)
              }
              commit("MERGE_TAGS_SUCCESS", updatedData);
            }
          },
          error => {
            Vue.notify({
              group: "store_notifications",
              type: "error",
              text: i18n.t("messages.request.error")
            });
            commit("MERGE_TAGS_FAIL", error);
            reject(error);
          }
        )
        .catch(error => {
          Vue.notify({
            group: "store_notifications",
            type: "error",
            text: i18n.t("messages.request.error")
          });

          commit("MERGE_TAGS_FAIL", error);
        })
        .finally(() => commit("MERGE_TAGS_BUSY", false));
    })
  },
  createOrganization: function({ commit, rootState }, { projectUid, row }) {
    commit("CREATE_TAG_BUSY", true);

    const { accessToken } = rootState.user.user.data;
    const config = {
        headers: { Authorization: `Token token=${accessToken}` }
    };

    return Axios.post(`${API_HOST}/analysis/${projectUid}/tags/${row.cat_id}/create/organization`, {}, config)
        .then(function(response) {
            commit("CREATE_TAG_SUCCESS", response.data);
        })
        .catch(function(error) {
            Vue.notify({
                group: "store_notifications",
                type: "error",
                text: i18n.t("messages.request.error")
            });
            commit("CREATE_TAG_FAIL", error);
        })
        .finally(function() {
            commit("CREATE_TAG_BUSY", false);
        });
  },

  deleteTagOrganization: ({ commit, rootState }, { projectUid, row }) => {

    commit("DELETE_TAG_BUSY", true);

    const body = {
      tagId: [row.id]
    };

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` },
      data: body
    };

    Axios.delete(
      `${API_HOST}/analysis/${projectUid}/tags/${row.cat_id}/delete/organization`,
      config
    )
      .then(() => {
        commit("DELETE_TAG_SUCCESS", row);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("DELETE_TAG_FAIL", error);
      })
      .finally(() => commit("DELETE_TAG_BUSY", false));
  },
  updateTagOrganization: ({ commit, rootState }, { projectUid, row }) => {
    commit("UPDATE_TAG_BUSY", true);

    const body = {
      ...row,
      tagId: row.id
    };

    if (body.tag_key != undefined) {
      body.tagKey = body.tag_key;
    }

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    return Axios.put(
      `${API_HOST}/analysis/${projectUid}/tags/${row.cat_id}/update/organization`,
      body,
      config
    )
      .then(response => {
        commit("UPDATE_TAG_SUCCESS", response.data);
        return response.data;
      })
      .catch(error => {
        const errorKey = error?.response?.data?.message;
        const i18nKey = errorKey ? `tag.form.errors.organization.${errorKey}` : null;
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18nKey && i18n.te(i18nKey) ? i18n.t(i18nKey) : i18n.t("messages.request.error")
        });

        console.error(error);
        commit("UPDATE_TAG_FAIL", { ...(error?.response?.data || error), cat_id: row.cat_id });
        return Promise.reject(error);
      })
      .finally(() => commit("UPDATE_TAG_BUSY", false));
  },

  getTagsUsageReport: ({ commit, rootState }, {projectId, pagination, filters}) => {
    if(pagination.page === 1){
      commit("LOAD_TAGS_USAGE_REPORT_BUSY", true);
    }else{
      commit("LOAD_TAGS_USAGE_REPORT_PAGINATION_BUSY", true);
    }
    
    const { accessToken } = rootState.user.user.data;

    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    const query = getUrlParams({ ...pagination, filters });
    const url = `${API_HOST}/analysis/${projectId}/tags/usage-report?${query}`;

    return Axios.get(url, config)
      .then( response =>{
        if (!response.data.error) {
          commit("LOAD_TAGS_USAGE_REPORT_SUCCESS", {
            tags: response.data.data.data, 
            pagination: response.data.data.pagination
          });
        }else{
          Vue.notify({
            group: "store_notifications",
            type: "error",
            text: i18n.t(`tag.form.errors.source.${response.data.error}`)
          });
        }
        return response.data;
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("LOAD_TAGS_USAGE_REPORT_FAIL", error);
      })
      .finally(() =>{
         commit("LOAD_TAGS_USAGE_REPORT_BUSY", false);
         commit("LOAD_TAGS_USAGE_REPORT_PAGINATION_BUSY", false);
      });
  },

  importOutlets: ({ commit, rootState }, { projectUid, payload }) => {
    commit("IMPORT_OUTLET_BUSY", true);

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    return Axios.post(
      `${API_HOST}/analysis/${projectUid}/tags/import_outlets`,
      payload,
      config
    )
      .then(response => {
        if (!response.data.error) {
          commit("IMPORT_OUTLET_SUCCESS", response.data);
        } else {
          Vue.notify({
            group: "store_notifications",
            type: "error",
            text: i18n.t(`tag.form.errors.default.${response.data.error}`)
          });
        }
        return response.data;
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("IMPORT_OUTLET_FAIL", error);
      })
      .finally(() => commit("IMPORT_OUTLET_BUSY", false));
  },

};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
};
